import React, { createContext, useCallback, useContext, useMemo, useState } from "react"
import type { SortState } from "../util/listFilters"
import { DEFAULT_LIST_PAGE_SIZE, ListPageSize } from "../component/tablePagination/listPagination"

/**
 * Persists per-list-page view state (search text, sort, applied filters) so it survives
 * in-app navigation (e.g. Hotels list → hotel detail → back to Hotels). Stored in memory
 * via Context only — intentionally NOT persisted across a full browser refresh.
 *
 * Each list page reads/writes a slice keyed by a stable LIST_VIEW_KEYS value through the
 * `useListView(key)` hook, which returns Dispatch-compatible setters (value OR updater).
 */

/** Stable keys, one per list page. */
export const LIST_VIEW_KEYS = {
    quotation: "quotation",
    hotels: "hotels",
    excursions: "excursions",
    places: "places",
    trips: "trips",
    guides: "guides",
    agents: "agents",
    agencies: "agencies",
    configurationUsers: "configurationUsers",
    configurationCities: "configurationCities",
} as const

export type ListViewKey = (typeof LIST_VIEW_KEYS)[keyof typeof LIST_VIEW_KEYS]

export interface ListViewState {
    /** Search box text. */
    search: string
    /** Applied filters: { groupKey: selectedValues[] }. */
    filters: Record<string, string[]>
    /** Dropdown-based sort value (used by Hotels' Price sort). */
    sortValue: string
    /** Column-header sort state (used by the shared Table sort). */
    sortState: SortState
    /** 1-based page for client-side list pagination. */
    page: number
    /** Rows per page. */
    pageSize: ListPageSize
}

const EMPTY_SORT_STATE: SortState = { key: null, direction: null }

/** Fresh default view. New object each call so callers never share mutable refs. */
const makeDefaultView = (): ListViewState => ({
    search: "",
    filters: {},
    sortValue: "default",
    sortState: { ...EMPTY_SORT_STATE },
    page: 1,
    pageSize: DEFAULT_LIST_PAGE_SIZE,
})

// Stable empty view returned when a key has no stored state yet (avoids new refs per render).
const DEFAULT_VIEW: ListViewState = Object.freeze(makeDefaultView()) as ListViewState

interface ListFilterContextType {
    views: Record<string, ListViewState>
    setField: <K extends keyof ListViewState>(
        key: string,
        field: K,
        action: React.SetStateAction<ListViewState[K]>,
    ) => void
}

const ListFilterContext = createContext<ListFilterContextType | undefined>(undefined)

export const ListFilterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [views, setViews] = useState<Record<string, ListViewState>>({})

    const setField = useCallback<ListFilterContextType["setField"]>((key, field, action) => {
        setViews((prev) => {
            const current = prev[key] ?? makeDefaultView()
            const prevVal = current[field]
            const nextVal =
                typeof action === "function"
                    ? (action as (p: ListViewState[typeof field]) => ListViewState[typeof field])(prevVal)
                    : action
            if (prevVal === nextVal) return prev
            return { ...prev, [key]: { ...current, [field]: nextVal } }
        })
    }, [])

    const value = useMemo<ListFilterContextType>(() => ({ views, setField }), [views, setField])

    return <ListFilterContext.Provider value={value}>{children}</ListFilterContext.Provider>
}

/** Setters mirror React.useState's Dispatch signature (accept a value or an updater fn). */
export interface UseListViewResult {
    search: string
    setSearch: React.Dispatch<React.SetStateAction<string>>
    filters: Record<string, string[]>
    setFilters: React.Dispatch<React.SetStateAction<Record<string, string[]>>>
    sortValue: string
    setSortValue: React.Dispatch<React.SetStateAction<string>>
    sortState: SortState
    setSortState: React.Dispatch<React.SetStateAction<SortState>>
    page: number
    setPage: React.Dispatch<React.SetStateAction<number>>
    pageSize: ListPageSize
    setPageSize: React.Dispatch<React.SetStateAction<ListPageSize>>
}

export function useListView(key: string): UseListViewResult {
    const ctx = useContext(ListFilterContext)
    if (!ctx) {
        throw new Error("useListView must be used within a ListFilterProvider")
    }
    const { views, setField } = ctx
    const view = views[key] ?? DEFAULT_VIEW

    const setSearch = useCallback<React.Dispatch<React.SetStateAction<string>>>(
        (a) => setField(key, "search", a),
        [setField, key],
    )
    const setFilters = useCallback<React.Dispatch<React.SetStateAction<Record<string, string[]>>>>(
        (a) => setField(key, "filters", a),
        [setField, key],
    )
    const setSortValue = useCallback<React.Dispatch<React.SetStateAction<string>>>(
        (a) => setField(key, "sortValue", a),
        [setField, key],
    )
    const setSortState = useCallback<React.Dispatch<React.SetStateAction<SortState>>>(
        (a) => setField(key, "sortState", a),
        [setField, key],
    )
    const setPage = useCallback<React.Dispatch<React.SetStateAction<number>>>(
        (a) => setField(key, "page", a),
        [setField, key],
    )
    const setPageSize = useCallback<React.Dispatch<React.SetStateAction<ListPageSize>>>(
        (a) => setField(key, "pageSize", a),
        [setField, key],
    )

    return {
        search: view.search,
        setSearch,
        filters: view.filters,
        setFilters,
        sortValue: view.sortValue,
        setSortValue,
        sortState: view.sortState,
        setSortState,
        page: view.page ?? 1,
        setPage,
        pageSize: view.pageSize ?? DEFAULT_LIST_PAGE_SIZE,
        setPageSize,
    }
}

export { ListFilterContext }
